/**
 * AuthContext -- provider-agnostic auth state.
 *
 * Delegates to Clerk, OIDC, or Local implementations based on the
 * REACT_APP_AUTH_PROVIDER environment variable.  All downstream code
 * continues to use `useAuth()` without changes.
 */

import { createContext, useContext, ReactNode } from 'react';
import { AUTH_PROVIDER } from '../config/auth';
import { AuthContextType } from './auth/types';
import { ClerkAuthProvider } from './auth/ClerkAuthContext';
import { OIDCAuthProvider } from './auth/OIDCAuthContext';
import { LocalAuthProvider } from './auth/LocalAuthContext';

// Re-export types so existing imports keep working
export type { User, ImpersonationContext, AuthContextType } from './auth/types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  switch (AUTH_PROVIDER) {
    case 'oidc':
      return <OIDCAuthProvider context={AuthContext}>{children}</OIDCAuthProvider>;
    case 'local':
      return <LocalAuthProvider context={AuthContext}>{children}</LocalAuthProvider>;
    case 'clerk':
    default:
      return <ClerkAuthProvider context={AuthContext}>{children}</ClerkAuthProvider>;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
